import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  FiHome,
  FiUserPlus,
  FiUsers,
  FiMessageSquare,
  FiBarChart2,
  FiSend,
  FiUser,
  FiFlag,
  FiShield,
  FiSettings
} from "react-icons/fi";

const AdminSidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasPermission, isSuperAdmin, hasAnyPermission } = useAuth();

  // Get the correct dashboard path based on user role
  const getDashboardPath = () => {
    if (user?.role === 'state-admin') {
      return "/admin/state-dashboard";
    } else if (user?.role === 'sd-admin') {
      return "/admin/senatorial-dashboard";
    } else if (user?.role === 'fc-admin') {
      return "/admin/federal-constituency-dashboard";
    } else if (user?.role === 'lga-admin') {
      return "/admin/lga-dashboard";
    } else if (user?.role === 'lcda-admin') {
      return "/admin/lcda-dashboard";
    } else if (user?.role === 'ward-admin') {
      return "/admin/ward-dashboard";
    } else if (user?.role === 'polling-unit-admin') {
      return "/admin/polling-unit-dashboard";
    } else {
      return "/admin/dashboard";
    }
  };

  // Check if current path is active
  const isActivePath = (path) => {
    if (path === "/admin/dashboard") {
      return location.pathname.includes("dashboard");
    }
    return location.pathname === path;
  };

  // Check if admin can create admins below them
  const canCreateAdmins = () => {
    if (isSuperAdmin) return true;
    if (user?.role === 'state-admin') return false;
    if (user?.role === 'sd-admin') {
      return hasAnyPermission(["create-fc-admin", "create-lga-admin", "create-lcda-admin"]);
    }
    if (user?.role === 'fc-admin') {
      return hasAnyPermission(["create-lcda-admin", "create-ward-admin"]);
    }
    if (user?.role === 'lga-admin') return false;
    if (user?.role === 'lcda-admin') return hasPermission("create-ward-admin");
    if (user?.role === 'ward-admin') return hasPermission("create-polling-unit-admin");
    return false;
  };

  // Check if admin can view other admins
  const canViewAdmins = () => {
    if (isSuperAdmin) return true;
    if (user?.role === 'state-admin') return true;
    if (user?.role === 'sd-admin') {
      return hasAnyPermission(["create-fc-admin", "create-lga-admin", "create-lcda-admin"]);
    }
    if (user?.role === 'fc-admin') {
      return hasAnyPermission(["create-lcda-admin", "create-ward-admin"]);
    }
    if (user?.role === 'lga-admin') return false;
    if (["lcda-admin", "ward-admin"].includes(user?.role)) return canCreateAdmins();
    return false;
  };

  // Check if admin can manage members
  const canManageMembers = () => {
    if (user?.role === 'lga-admin') {
      return hasAnyPermission([
        "view-members", "create-members", "edit-members", "delete-members",
        "approve-members", "suspend-members", "reject-members", "reactivate-members"
      ]);
    }
    return hasAnyPermission([
      "view-members", "create-members", "edit-members", "delete-members",
      "approve-members", "suspend-members", "reject-members", "reactivate-members"
    ]);
  };

  // Check if admin can broadcast
  const canBroadcast = () => {
    if (user?.role === 'lga-admin') {
      return hasAnyPermission([
        "broadcast-announcement-local-government",
        "broadcast-announcement-lcda",
        "broadcast-announcement-ward",
        "broadcast-announcement-polling-unit",
        "broadcast-request-local-government",
        "broadcast-request-lcda",
        "broadcast-request-ward",
        "broadcast-request-polling-unit"
      ]);
    }
    return hasAnyPermission([
      "broadcast-announcement-nationwide",
      "broadcast-announcement-state",
      "broadcast-announcement-senatorial-district",
      "broadcast-announcement-federal-constituency",
      "broadcast-announcement-local-government",
      "broadcast-announcement-lcda",
      "broadcast-announcement-ward",
      "broadcast-announcement-polling-unit",
      "broadcast-request-nationwide",
      "broadcast-request-state",
      "broadcast-request-senatorial-district",
      "broadcast-request-federal-constituency",
      "broadcast-request-local-government",
      "broadcast-request-lcda",
      "broadcast-request-ward",
      "broadcast-request-polling-unit"
    ]);
  };

  // Check if admin can create group chats
  const canCreateGroupChats = () => {
    if (user?.role === 'lga-admin') {
      return hasPermission("create-group-chat-local-government");
    }
    return hasAnyPermission([
      "create-group-chat-nationwide",
      "create-group-chat-state",
      "create-group-chat-senatorial-district",
      "create-group-chat-federal-constituency",
      "create-group-chat-local-government",
      "create-group-chat-lcda",
      "create-group-chat-ward",
      "create-group-chat-polling-unit"
    ]);
  };

  // Check if admin can view reports
  const canViewReports = () => {
    if (user?.role === 'lga-admin') {
      return hasAnyPermission([
        "view-reports",
        "view-lga-reports",
        "view-lcda-reports",
        "view-ward-reports",
        "view-polling-unit-reports"
      ]);
    }
    return hasAnyPermission([
      "view-reports",
      "view-nationwide-reports",
      "view-state-reports",
      "view-sd-reports",
      "view-fc-reports",
      "view-lga-reports",
      "view-lcda-reports",
      "view-ward-reports",
      "view-polling-unit-reports"
    ]);
  };

  // Get menu items based on permissions
  const getMenuItems = () => {
    const dashboardPath = getDashboardPath();
    
    const items = [
      // Dashboard - Always available for all admins
      { 
        name: "Dashboard", 
        path: dashboardPath, 
        icon: <FiHome />,
        allowed: true
      },
      
      // Profile - Always available for all admins
      {
        name: "Profile",
        path: "/admin/profile",
        icon: <FiUser />,
        allowed: true
      },

      // Members Management
      {
        name: "View Members",
        path: "/admin/view-members",
        icon: <FiUsers />,
        allowed: canManageMembers()
      },

      // Admin Management
      {
        name: "Permissions",
        path: "/admin/create-admin",
        icon: <FiUserPlus />,
        allowed: canCreateAdmins() && user?.role !== 'lga-admin'
      },
      {
        name: "View Admins",
        path: "/admin/view-admins",
        icon: <FiUsers />,
        allowed: canViewAdmins() && user?.role !== 'lga-admin'
      },

      // Communication
      {
        name: "Chat",
        path: "/admin/create-group-chat",
        icon: <FiMessageSquare />,
        allowed: canCreateGroupChats()
      },
      {
        name: "Broadcast",
        path: "/admin/broadcast",
        icon: <FiSend />,
        allowed: canBroadcast()
      },

      // Reports & Analytics
      {
        name: "Reports",
        path: "/admin/reports",
        icon: <FiBarChart2 />,
        allowed: canViewReports()
      },

      // System Management (Super Admin only)
      {
        name: "Create Custom Role",
        path: "/admin/create-custom-role",
        icon: <FiShield />,
        allowed: user?.role === 'super-admin'
      },

      {
        name: "Brand Identity",
        path: "/admin/manage-brand",
        icon: <FiFlag />,
        allowed: user?.role === 'super-admin' && hasPermission("manage-branding")
      },
    ];

    return items.filter(item => item.allowed);
  };

  const handleItemClick = (path) => {
    navigate(path);
    if (window.innerWidth < 768) {
      onClose();
    }
  };

  const menuItems = getMenuItems();

  // Format role display name
  const getRoleDisplayName = () => {
    if (!user?.role) return 'Administration';
    
    const roleMap = {
      'super-admin': 'SUPER ADMIN',
      'state-admin': 'STATE ADMIN',
      'sd-admin': 'SENATORIAL DISTRICT ADMIN',
      'fc-admin': 'FEDERAL CONSTITUENCY ADMIN',
      'lga-admin': 'LOCAL GOVERNMENT ADMIN',
      'lcda-admin': 'LCDA ADMIN',
      'ward-admin': 'WARD ADMIN',
      'polling-unit-admin': 'POLLING UNIT ADMIN'
    };
    
    return roleMap[user.role] || user.role.replace('-', ' ').toUpperCase();
  };

  return (
    <>
      <style>
        {`
          .admin-sidebar {
            width: 280px;
            background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
            padding: 0;
            display: flex;
            flex-direction: column;
            height: 100vh;
            position: fixed;
            left: 0;
            top: 0;
            overflow-y: auto;
            z-index: 1000;
            transform: translateX(-100%);
            transition: transform 0.3s ease-in-out;
            border-right: 1px solid #334155;
          }

          .admin-sidebar.open {
            transform: translateX(0);
          }

          .sidebar-header {
            padding: 30px 25px 25px;
            background: rgba(15, 23, 42, 0.95);
            border-bottom: 1px solid #334155;
          }

          .sidebar-header h1 {
            font-weight: 700;
            color: #ffffff;
            font-size: 22px;
            margin-bottom: 8px;
            letter-spacing: 0.5px;
          }

          .role-display {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .role-badge {
            background: linear-gradient(45deg, #f59e0b, #d97706);
            color: white;
            padding: 4px 10px;
            border-radius: 20px;
            font-size: 10px;
            font-weight: 700;
          }

          .role-badge.super-admin {
            background: linear-gradient(45deg, #ec4899, #db2777);
          }

          .role-badge.state-admin {
            background: linear-gradient(45deg, #10b981, #059669);
          }

          .role-badge.sd-admin {
            background: linear-gradient(45deg, #3b82f6, #2563eb);
          }

          .role-badge.fc-admin {
            background: linear-gradient(45deg, #8b5cf6, #7c3aed);
          }

          .role-badge.lga-admin {
            background: linear-gradient(45deg, #f97316, #ea580c);
          }

          .sidebar-nav {
            flex: 1;
            padding: 20px 0;
          }

          .sidebar-item {
            display: flex;
            align-items: center;
            gap: 12px;
            font-size: 14px;
            color: #cbd5e1;
            cursor: pointer;
            padding: 12px 25px;
            font-weight: 500;
            background-color: transparent;
            margin-bottom: 2px;
            position: relative;
            transition: all 0.2s ease;
            border: none;
            width: 100%;
            text-align: left;
          }

          .sidebar-item:hover {
            background: rgba(255, 255, 255, 0.05);
            color: #ffffff;
          }

          .sidebar-item.active {
            color: #ffffff;
            font-weight: 600;
            background: rgba(59, 130, 246, 0.15);
            border-right: 3px solid #3b82f6;
          }

          .sidebar-item.active::before {
            display: none;
          }

          .sidebar-item-icon {
            font-size: 16px;
            flex-shrink: 0;
            width: 20px;
            text-align: center;
          }

          .sidebar-item.active .sidebar-item-icon {
            color: #3b82f6;
          }

          .sidebar-overlay {
            display: block;
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 999;
          }

          @media (min-width: 768px) {
            .admin-sidebar {
              transform: translateX(0);
            }
            .sidebar-overlay {
              display: none;
            }
          }

          @media (max-width: 767px) {
            .admin-sidebar {
              width: 300px;
            }
          }

          .admin-sidebar::-webkit-scrollbar {
            width: 6px;
          }
          .admin-sidebar::-webkit-scrollbar-track {
            background: #1e293b;
          }
          .admin-sidebar::-webkit-scrollbar-thumb {
            background: #475569;
            border-radius: 3px;
          }
          .admin-sidebar::-webkit-scrollbar-thumb:hover {
            background: #64748b;
          }

          .no-menu-items {
            padding: 40px 25px;
            text-align: center;
            color: #64748b;
          }

          .no-menu-items p {
            margin: 0;
            font-size: 14px;
          }

          .debug-info {
            margin-top: auto;
            padding: 15px 25px;
            font-size: 10px;
            color: #475569;
            border-top: 1px solid #334155;
            background: rgba(15, 23, 42, 0.8);
          }
        `}
      </style>

      {/* Overlay for Mobile */}
      {isOpen && <div className="sidebar-overlay" onClick={onClose}></div>}

      {/* Sidebar */}
      <div className={`admin-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h1>Admin Portal</h1>
          <div className="role-display">
            <span className={`role-badge ${user?.role?.replace('-', '-')}`}>
              {getRoleDisplayName()}
            </span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {menuItems.length > 0 ? (
            <>
              {menuItems.map((item, index) => (
                <button
                  key={index}
                  className={`sidebar-item ${isActivePath(item.path) ? 'active' : ''}`}
                  onClick={() => handleItemClick(item.path)}
                >
                  <div className="sidebar-item-icon">{item.icon}</div>
                  <span>{item.name}</span>
                </button>
              ))}
            </>
          ) : (
            <div className="no-menu-items">
              <p>No menu items available for your role.</p>
              <p style={{ fontSize: '12px', marginTop: '10px' }}>
                Role: {user?.role || 'Unknown'}
              </p>
            </div>
          )}
        </nav>
      </div>
    </>
  );
};

export default AdminSidebar;